/* eslint-disable no-param-reassign */
/* eslint-disable react/no-deprecated */
/**
 * @author Fernando Ruano
 * @file index.js
 * @description AppTree Component
 */

// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { Icon } from 'antd';
import InfinityMenu from 'react-infinity-menu';
import { UpOutlined, DownOutlined } from '@ant-design/icons';
import _ from 'lodash';

// Styles
import 'react-infinity-menu/src/infinity-menu.css';
import styles from './style.module.css';

/**
 * AppTree Function component.
 * @param { Object } props - React PropTypes
 * @property { String } className - className string.
 * @return { Node } React node containing AppTree.
 */
export const TreeItem = ({
  className = '',
  onClick,
  label,
  isOpen,
  // toggleNode,
  level = 0,
  last,
  loading,
  icon,
  style = {},
  data
}) => {
  const hasNodes = data?.children?.length > 0;
  const active = !hasNodes && isOpen;
  const classNames = [
    className,
    styles.treeItem,
    active ? styles.active : '',
    level ? styles.innerLevelItem : '',
    last ? styles.last : '',
    isOpen && hasNodes ? styles.isOpen : '',
    isOpen && level && hasNodes ? styles.isOpenInner : '',
    last && level ? styles.lastInner : '',
    last && level ? styles.lastInnerBoth : ''
  ];
  return (
    <li
      onClick={onClick}
      className={classNames.join(' ')}
      style={{ ...style, marginLeft: level * 10 }}>
      {icon && icon !== undefined && (
        <div className="mr-1">
          <Icon type={icon} />
        </div>
      )}
      <Icon
        type="message"
        style={{ fontSize: '16px', color: '#08c' }}
        theme="outlined"
      />
      {label}
      <div>
        {loading && (
          <div>
            <Icon type="loading" />
          </div>
        )}
        {hasNodes && <div>{isOpen ? <UpOutlined /> : <DownOutlined />}</div>}
      </div>
    </li>
  );
};

/**
 * @name TreeItem.propTypes
 * @type { propTypes }
 * @property { String } className - className string.
 */
TreeItem.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  label: PropTypes.string,
  isOpen: PropTypes.bool,
  toggleNode: PropTypes.func,
  level: PropTypes.number,
  last: PropTypes.bool,
  loading: PropTypes.bool,
  icon: PropTypes.string,
  style: PropTypes.object,
  data: PropTypes.object
};

/**
 * AppTree Function component.
 * @param { Object } props - React PropTypes
 * @property { String } className - className string.
 * @return { Node } React node containing AppTree.
 */
class AppTree extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      data: this.props.data,
      initialData: []
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.data !== this.props.data) {
      this.setState({
        data: this.props.data
      });
    }
  }

  componentDidMount() {
    const originData = _.cloneDeep(this.props.data);
    this.setState({
      initialData: originData
    });
  }

  onSingleSelection(tree, node, keypath) {
    let entireObject = _.cloneDeep(this.state.initialData);
    const indexPath = keypath.split('.');
    let objectItem;

    if (indexPath.length > 1) {
      entireObject = _.cloneDeep(tree);

      objectItem = _.get(tree, indexPath.slice(0, -1));

      objectItem.forEach((element) => {
        if (element !== node) {
          element.isOpen = false;
        }
      }, _.set(entireObject, indexPath.slice(0, -1), objectItem));
    } else {
      objectItem = _.set(entireObject, keypath, node);
    }

    this.setState({
      data: entireObject
    });
  }

  onMultipleSelection(tree, node, keypath) {
    const entireObject = _.cloneDeep(this.state.initialData);
    let objectItem;
    if (this.props.mainSelectableItem.includes(node.name)) {
      objectItem = _.set(entireObject, keypath, node);
    } else {
      objectItem = tree;
    }
    this.setState({
      data: objectItem
    });
  }

  onNodeMouseClick(event, tree, node, level, keypath) {
    if (this.props.multipleSelection) {
      this.onMultipleSelection(tree, node, keypath);
    } else {
      this.onSingleSelection(tree, node, keypath);
    }
    if (node.children.length === 0) {
      this.props.onSelectItem(node, tree);
    }
  }

  render() {
    return (
      <InfinityMenu
        disableDefaultHeaderContent={true}
        tree={this.state.data}
        onNodeMouseClick={this.onNodeMouseClick.bind(this)}
      />
    );
  }
}

/**
 * @name AppTree.propTypes
 * @type { propTypes }
 * @property { String } className - className string.
 */
AppTree.propTypes = {
  className: PropTypes.string,
  data: PropTypes.array,
  onClickItem: PropTypes.func,
  initialActive: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  initialOpenNodes: PropTypes.array,
  onSelectItem: PropTypes.func,
  openNodes: PropTypes.array,
  onLoadData: PropTypes.bool,
  multipleSelection: PropTypes.bool,
  mainSelectableItem: PropTypes.array
};

/**
 * AppTree default props.
 */
AppTree.defaultProps = {
  className: '',
  data: [],
  initialActive: null,
  initialOpenNodes: [],
  openNodes: [],
  onClickItem: (props) => props,
  multipleSelection: false
};

export default AppTree;
